/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type { ApplicationPermissions } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import { PREMIUM_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import type { PreloadedQuery } from 'react-relay';
import { getProjectKeyOrId } from '@jira/router-resources__utils/src/getProjectKeyOrId.tsx';
import QUERY_NEW, {
	type TopNavigationQuery,
} from '@jira/navigation-apps__atlassian-navigation/src/ui/__generated__/TopNavigationQuery.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import {
	createRelayResource,
	RELAY_RESOURCE_TYPE,
} from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { RouteResource } from '@atlassian/react-resource-router';
import { PlatformSiteAri } from '@atlassian/ari/platform/site';

const hasOnlyJSMAndCSMAccess = (appPermissions: ApplicationPermissions) => {
	const {
		hasServiceDeskAccess,
		hasCustomerServiceAccess,
		hasSoftwareAccess,
		hasProductDiscoveryAccess,
	} = appPermissions;

	return (
		hasServiceDeskAccess &&
		hasCustomerServiceAccess &&
		!hasSoftwareAccess &&
		!hasProductDiscoveryAccess
	);
};

export const topNav4UIStateResource: RouteResource<
	PreloadedQuery<TopNavigationQuery, Record<string, unknown>>
> = createRelayResource<TopNavigationQuery>({
	type: `${RELAY_RESOURCE_TYPE}_TOPNAV_UI_STATE_RELAY`,
	getQuery: (
		{ match, route },
		{ tenantContext: { cloudId, isAnonymous, atlassianAccountId, appPermissions, appEditions } },
	) => {
		const collabContextRoutingAri = fg('post-office_enable_routing_workspace_id_prop')
			? PlatformSiteAri.create({ siteId: cloudId }).toString()
			: undefined;
		const issueKey = match.params.issueKey ?? '';
		const projectKey = match.params.projectKey ?? '';
		const projectIdOrKey = getProjectKeyOrId(match);

		const isCsmRoute =
			match.path.startsWith('/jira/csm') ||
			match.path.startsWith('/jira/helpcenter') || // note: remove when cleaning csm_appification_standalone_navigation
			match.path.startsWith('/jira/customer-service'); // note: remove when cleaning csm_appification_standalone_navigation

		return {
			parameters: QUERY_NEW,
			variables: {
				cloudId,
				isAnonymous,
				atlassianAccountId: atlassianAccountId ?? '',
				isNavStateQuerySkipped:
					isAnonymous ||
					fg('navx-global-side-nav') ||
					expVal('navx-full-height-sidebar-exp', 'isEnabled', false),
				issueKey: issueKey ?? '',
				shouldCheckProjectTypeForIssue:
					route.group === 'issue' && Boolean(issueKey) && hasOnlyJSMAndCSMAccess(appPermissions),
				projectKey: projectKey ?? '',
				shouldCheckProjectType: Boolean(projectKey) && hasOnlyJSMAndCSMAccess(appPermissions),
				projectIdOrKey,
				shouldFetchProjectTypeForSettingsAndEA: Boolean(projectIdOrKey),

				// we can SSR csmLiveChatAgent status indicator
				shouldFetchCSMLiveChatAgent:
					appPermissions.hasCustomerServiceAccess &&
					appEditions.customerService === PREMIUM_EDITION &&
					isCsmRoute &&
					fg('csm-omni-channel-live-chat-in-widget'),
				// we can't SSR csmLiveChatAgent
				// let's get the project type to check if we should mount csmLiveChatAgent status indicator
				// note: remove when cleaning csm_appification_standalone_navigation
				shouldFetchProjectType:
					appPermissions.hasCustomerServiceAccess &&
					appEditions.customerService === PREMIUM_EDITION &&
					Boolean(projectIdOrKey) &&
					!isCsmRoute &&
					fg('csm-omni-channel-live-chat-in-widget'),

				skipHelpButtonRelayRead: isAnonymous,
				collabContextRoutingAri,
			},
			options: { fetchPolicy: 'store-and-network' },
		};
	},
	isCritical: true,
});
